import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import '../../CSS/Global.css';
import { Document, Page, Text, View, StyleSheet, Image, Link, pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useParams } from "react-router-dom";
import logoImgText from '../../Img/100BILLION_TECH_name.png';
import logoImg from '../../Img/100BILLION_ICON.png';
import { getDecryptedUserFromCookie } from '../utils/getCookie';
import Swal from 'sweetalert2';
import { parseDocument } from 'htmlparser2';
import { DomUtils } from 'htmlparser2';
import dayjs from "dayjs";
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Download,
    FileDown,
    Search,
    X
} from "lucide-react";
import { useSearch } from '../../SearchContext';
import { useFilter } from '../../FilterContext';
import FilterDrawer from '../FilterDrawer';
import Loader from '../Loader';
import { motion } from 'framer-motion';
import { decryptToken } from '../utils/getToken';
import OmsSpinnerData from '../Loader/Spinner';



const IndividualInvoice = () => {
    const navigate = useNavigate();
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [isImportOpen, setImportOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("All invoice");
    const [invoices, setInvoices] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [downloadedId, setDownloadedId] = useState(null);
    const [load, setLoad] = useState(false)
    const url = API_BASE_URL
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const [currentPage, setCurrentPage] = useState(1);
    const { globalSearch } = useSearch();
    const { entriesPerPage, sortOrder, showDrawer, setShowDrawer, customDate } = useFilter();
    const [rangeType, setRangeType] = useState('0');
    const [customRange, setCustomRange] = useState({ from: '', to: '' });
    const token = decryptToken("accessToken") || ""
    const [showSearchBar, setShowSearchBar] = useState(false);
    const { id } = useParams();
    const [authorityData, setAuthorityData] = useState({});
    const [bankDetails, setBankDetails] = useState({})
    const [customer, setCustomer] = useState({})

    const safeDate = (dateStr) => {
        try {
            if (!dateStr || dateStr === "0000-00-00") return "N/A";

            const parsedDate = new Date(dateStr);
            if (isNaN(parsedDate.getTime())) return "N/A";

            return parsedDate.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const outstandingAmount = (inv) =>
        parseFloat(
            inv.total_pending ?? inv.pending ?? inv.adjusted_pending ?? 0
        ) || 0;

    const grandTotalAmount = (inv) =>
        parseFloat(inv.total_grand_total ?? inv.grand_total ?? 0) || 0;

    const collectedAmount = (inv) => {
        const g = grandTotalAmount(inv);
        const o = outstandingAmount(inv);
        return Math.max(0, g - o);
    };

    const invoiceActivityDate = (inv) => {
        const raw =
            inv.createdAt ||
            inv.invoice_date ||
            inv.created_range?.to ||
            inv.created_range?.from;
        const parsed = raw ? new Date(raw) : null;
        return parsed && !isNaN(parsed.getTime()) ? parsed : null;
    };

    const invoicesForSummary = Array.isArray(invoices)
        ? invoices.filter(inv => inv.dpstatus !== "Draft")
        : [];

    const totalReceivablesPending = invoicesForSummary.reduce(
        (acc, invoice) => acc + outstandingAmount(invoice),
        0
    );

    const today = new Date().toISOString().split("T")[0];

    const dueTodayPendingTotal = invoices.reduce((acc, invoice) => {
        if (!invoice.due_date || invoice.due_date === "0000-00-00") return acc;

        const dueDateObj = new Date(invoice.due_date);
        if (isNaN(dueDateObj.getTime())) return acc;

        const dueDate = dueDateObj.toISOString().split("T")[0];
        return dueDate === today ? acc + outstandingAmount(invoice) : acc;
    }, 0);


    const handleEdit = (invoice) => {
        navigate('/new-invoice', { state: { invoice } });
    };

    const resolveInvoiceCustomerId = (inv) =>
        inv?.customer_id ?? inv?.cust_ID ?? inv?.customerId ?? id ?? null;

    const handleCreateReceipt = (invoice) => {
        const pending = outstandingAmount(invoice);
        const today = new Date().toLocaleDateString('en-CA');
        navigate('/receipt', {
            state: {
                openReceipt: true,
                prefillReceipt: {
                    customerId: resolveInvoiceCustomerId(invoice),
                    customerName: invoice.customer_name || '',
                    invoiceNumber: invoice.invoice_number || '',
                    amount: pending > 0 ? pending.toFixed(2) : '',
                    receiptDate: today,
                },
            },
        });
    };

    const STATUS_COLORS = {
        Paid: "green",
        Pending: "gray",
        Overdue: "red",
    };

    const backendStatusLabel = (invoice) => {
        const raw = invoice?.status;
        if (raw == null || String(raw).trim() === "") return "N/A";
        return String(raw).trim();
    };

    const overduePendingTotal = invoices.reduce((acc, invoice) => {
        if (invoice.dpstatus === "Draft") return acc;
        return backendStatusLabel(invoice) === "Overdue"
            ? acc + outstandingAmount(invoice)
            : acc;
    }, 0);

    const payableWithin30DaysTotal = invoices.reduce((acc, invoice) => {
        if (invoice.dpstatus === "Draft") return acc;
        const dueDate = new Date(invoice.due_date);
        const today = new Date();
        const diffInDays = (dueDate - today) / (1000 * 60 * 60 * 24);

        if (diffInDays > 0 && diffInDays <= 30) {
            return acc + outstandingAmount(invoice);
        }
        return acc;
    }, 0);

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoad(true); // start loading
            try {
                const res = await fetch(`${url}/newinvoice/getviacustomall/${id}`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                const data = await res.json();
                console.log("Invoice Details:", data);
                if (Array.isArray(data)) {
                    const sortedInvoices = data.sort((a, b) => a.order_number - b.order_number).reverse();
                    setInvoices(sortedInvoices);
                } else {
                    console.error("Fetched data is not an array:", data);
                    setInvoices([]);
                }
            } catch (error) {
                console.error("Failed to fetch invoices:", error);
                setInvoices([]);
            } finally {
                setLoad(false); // stop loading no matter success or failure
            }
        };

        fetchInvoices();
    }, []);



    // Toggle the dropdown list
    const toggleDropdown = () => {
        setDropdownOpen(!isDropdownOpen);
    };

    // Toggle the import list
    const toggleImport = () => {
        setImportOpen(!isImportOpen);
    };

    // Handle search input change
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    let filteredInvoices = [...invoices];

    // 1. Filter by date
    if (customDate.from || customDate.to) {
        filteredInvoices = filteredInvoices.filter((invoice) => {
            const createdAt = new Date(invoice.createdAt || invoice.invoice_date); // fallback
            const from = customDate.from ? new Date(customDate.from) : null;
            const to = customDate.to ? new Date(customDate.to) : null;

            return (!from || createdAt >= from) && (!to || createdAt <= to);
        });
    }

    // 2. Sort by date
    filteredInvoices.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.invoice_date);
        const dateB = new Date(b.createdAt || b.invoice_date);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    const searchText = showSearchBar ? searchQuery : globalSearch;

    // 3. Global Search + Filter
    filteredInvoices = filteredInvoices.filter((invoice) => {
        const search = searchText.toLowerCase();
        const status = backendStatusLabel(invoice);
        const createdBy = invoice.created_by?.toLowerCase() || "import";

        const matchesSearch = (
            invoice.customer_name?.toLowerCase().includes(search) ||
            invoice.invoice_number?.toLowerCase().includes(search) ||
            invoice.order_number?.toString().includes(search) ||
            invoice.invoice_date?.toLowerCase().includes(search) ||
            invoice.due_date?.toLowerCase().includes(search)
        );

        if (!matchesSearch) return false;

        switch (selectedFilter) {
            case "Active invoice":
                return status === "Pending";
            case "Paid invoice":
                return status === "Paid";
            case "Overdue invoice":
                return status === "Overdue";
            case "My invoice":
                return createdBy === "admin";
            case "My Active invoice":
                return createdBy === "admin" && status === "Pending";
            case "My Paid invoice":
                return createdBy === "admin" && status === "Paid";
            case "My Overdue invoice":
                return createdBy === "admin" && status === "Overdue";
            case "All invoice":
                return true;
            default:
                // treat any unknown filter as "All invoice"
                return true;
        }
    });

    const SkeletonLoader = () => {
        return (
            <div>
                {Array(10)
                    .fill("")
                    .map((_, index) => (
                        <div className="skeleton-row" key={index}>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                            <div className="skeleton"></div>
                        </div>
                    ))}
            </div>
        );
    };

    const handleRefresh = () => {
        setLoading(true); // Start skeleton loader
        setTimeout(() => {
            setLoading(false); // End skeleton loader after data "refresh"
        }, 1000); // Simulate API refresh delay (2 seconds)
    };

    const PLACEHOLDER_LOGO_DATA_URI =
        "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==";

    const toBase64 = async (resourceUrl, fetchOpts = {}) => {
        const response = await fetch(resourceUrl, {
            mode: "cors",
            credentials: "omit",
            ...fetchOpts,
        });
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const fetchBankDetails = async () => {
        try {
            const response = await fetch(`${url}/bank-detail/get`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            setBankDetails(res.data || {});

        } catch (error) {
            console.error(error)
        }
    }

    const fetchAuthoritySign = async () => {
        try {
            const response = await fetch(`${url}/authority-sign/get`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            })
            const res = await response.json();
            setAuthorityData({
                sign: res.data.sign
                    ? res.data.sign.replace(/^\/?uploads\/signatures\//, "")
                    : "",
                company_name: res.data.company_name,
            });

        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        fetchAuthoritySign();
        fetchBankDetails();
    }, [])




    const downloadInvoicePDF = async (invoice) => {
        const orderId = invoice.order_number;
        setDownloadingId(orderId);
        setDownloadedId(null);
        const customerForPdf = await fetchCustomerSingle(resolveInvoiceCustomerId(invoice));
        if (!customerForPdf) {
            alert("Customer is missing for this invoice; cannot build PDF.");
            setDownloadingId(null);
            return;
        }
        const mainId = isLoggedIn?.main_id;
        if (!mainId) {
            alert("Session is missing business id; log in again and retry.");
            setDownloadingId(null);
            return;
        }
        try {
            const profileRes = await fetch(`${url}/register/single/${mainId}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const res = await profileRes.json();
            if (!profileRes.ok || !res.users) {
                alert(
                    res?.message ||
                        `Could not load business profile (${profileRes.status}).`
                );
                return;
            }
            const profileData = res.users;

            let base64Logo = null;
            let base64Sign = null;
            if (authorityData?.sign) {
                try {
                    base64Sign = await toBase64(
                        `${url}/signatures/${authorityData.sign}`
                    );
                } catch (e) {
                    console.warn("Signature load failed:", e);
                }
            }
            if (profileData?.profilePic) {
                const imageUrl = `${url}/logo/${profileData.profilePic}`;
                try {
                    base64Logo = await toBase64(imageUrl);
                } catch (error) {
                    console.warn("Logo conversion failed:", error);
                    base64Logo = PLACEHOLDER_LOGO_DATA_URI;
                }
            }
            const blob = await pdf(
                <InvoiceDocument invoice={invoice} profile={profileData} logoBase64={base64Logo} bankDetails={bankDetails} authorityData={authorityData} base64Sign={base64Sign} customer={customerForPdf} />
            ).toBlob();
            saveAs(blob, `Invoice_${invoice.invoice_number}.pdf`);

            setDownloadedId(orderId);
            setTimeout(() => setDownloadedId(null), 1200);
        } catch (err) {
            alert(
                err?.message
                    ? `Failed to download invoice: ${err.message}`
                    : "Failed to download invoice"
            );
            console.error("Download error:", err);
        } finally {
            setDownloadingId(null);
        }
    };

    const fetchCustomerSingle = async (customerId) => {
        const id =
            customerId !== undefined &&
            customerId !== null &&
            String(customerId).trim() !== "" &&
            String(customerId) !== "undefined"
                ? String(customerId).trim()
                : null;
        if (!id) return null;
        try {
            const response = await fetch(
                `${url}/api/customer/single/${encodeURIComponent(id)}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const res = await response.json();
            if (!response.ok) {
                console.warn("fetchCustomerSingle:", res?.error || response.status);
                return null;
            }

            const c = res.customer;
            setCustomer(c || {});
            return c || null;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const viewInvoicePDF = async (invoice) => {
        const orderId = invoice.order_number;
        setDownloadingId(orderId);
        setDownloadedId(null);
        const customerData = await fetchCustomerSingle(resolveInvoiceCustomerId(invoice));

        if (!customerData) {
            alert("Customer not loaded");
            return;
        }
        const mainIdView = isLoggedIn?.main_id;
        if (!mainIdView) {
            alert("Session is missing business id; log in again and retry.");
            setDownloadingId(null);
            return;
        }
        try {
            const profileRes = await fetch(`${url}/register/single/${mainIdView}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            const res = await profileRes.json();
            if (!profileRes.ok || !res.users) {
                alert(
                    res?.message ||
                        `Could not load business profile (${profileRes.status}).`
                );
                return;
            }
            const profileData = res.users;

            let base64Logo = null;
            let base64Sign = null;
            if (authorityData?.sign) {
                try {
                    base64Sign = await toBase64(
                        `${url}/signatures/${authorityData.sign}`
                    );
                } catch (e) {
                    console.warn("Signature load failed:", e);
                }
            }
            if (profileData?.profilePic) {
                const imageUrl = `${url}/logo/${profileData.profilePic}`;
                try {
                    base64Logo = await toBase64(imageUrl);
                } catch (error) {
                    console.warn("Logo conversion failed:", error);
                    base64Logo = PLACEHOLDER_LOGO_DATA_URI;
                }
            }
            const blob = await pdf(
                <InvoiceDocument invoice={invoice} profile={profileData} logoBase64={base64Logo} bankDetails={bankDetails} authorityData={authorityData} base64Sign={base64Sign} customer={customerData} />
            ).toBlob();
            const fileURL = URL.createObjectURL(blob);
            window.open(fileURL, "_blank");
            setDownloadedId(orderId);
            setTimeout(() => setDownloadedId(null), 1200);
        } catch (err) {
            alert(
                err?.message
                    ? `Failed to open invoice PDF: ${err.message}`
                    : "Failed to open invoice PDF"
            );
            console.error("Download error:", err);
        } finally {
            setDownloadingId(null);
        }
    };


    // useEffect(() => {
    //     fetch("https://ecommerce.100billionoms.com/logo/1749467908154-perfumelogo.png", {
    //         mode: "cors"
    //     })
    //         .then((res) => {
    //             console.log("Fetch success:", res.status);
    //             return res.blob();
    //         })
    //         .then((blob) => console.log("Blob:", blob))
    //         .catch((err) => console.error("Fetch failed:", err));
    // }, []);
    const handleDelete = async (customerId) => {
        console.log(customerId)
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`${url}/newinvoice/${customerId}`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    });
                    if (!response.ok) {
                        const err = await response.json();
                        throw new Error(err?.message || 'Failed to delete');
                    }

                    Swal.fire('Deleted!', 'Invoice has been deleted.', 'success');
                    setInvoices((prev) => prev.filter((item) => item.id !== customerId));
                } catch (error) {
                    console.error('Delete error:', error.message);
                    Swal.fire('Error!', `Failed to delete invoice. ${error.message}`, 'error');
                }
            }
        });
    };

    const invoiceInSummaryDateRange = (inv) => {
        const created = invoiceActivityDate(inv);
        if (!created) return false;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (rangeType === "custom") {
            if (!customRange.from || !customRange.to) return true;
            const from = new Date(customRange.from);
            const to = new Date(customRange.to);
            to.setHours(23, 59, 59, 999);
            return created >= from && created <= to;
        }

        const createdDate = new Date(created);
        createdDate.setHours(0, 0, 0, 0);

        if (rangeType === "0") {
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const todayEnd = new Date();
            todayEnd.setHours(23, 59, 59, 999);
            return created >= todayStart && created <= todayEnd;
        }

        if (rangeType === "1") {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);
            return created >= yesterday && created <= yesterdayEnd;
        }

        const diff = Math.floor(
            (today - createdDate) / (1000 * 60 * 60 * 24)
        );
        return diff <= parseInt(rangeType, 10);
    };

    useEffect(() => {
        if (rangeType !== 'custom') {
            setCustomRange({ from: '', to: '' });
        }
    }, [rangeType]);

    useEffect(() => {
        setCurrentPage(1);
    }, [rangeType, customRange.from, customRange.to]);

    // Date range filter disabled — list shows all invoices for this customer (search/tab filters still apply)
    // const summaryInvoices = filteredInvoices.filter((inv) =>
    //     invoiceInSummaryDateRange(inv)
    // );
    const summaryInvoices = filteredInvoices;

    let paidAmount = 0;
    let pendingAmount = 0;
    let overdueAmount = 0;

    const todayYmd = new Date().toISOString().split("T")[0];

    summaryInvoices.forEach((inv) => {
        const outstanding = outstandingAmount(inv);
        const collected = collectedAmount(inv);
        paidAmount += collected;

        if (outstanding <= 0) return;

        const dueRaw = inv.due_date;
        let isOverdue = false;
        if (dueRaw && dueRaw !== "0000-00-00") {
            try {
                const dueYmd = new Date(dueRaw).toISOString().split("T")[0];
                isOverdue = dueYmd < todayYmd;
            } catch {
                isOverdue = false;
            }
        }

        if (isOverdue) overdueAmount += outstanding;
        else pendingAmount += outstanding;
    });

    const total = paidAmount + pendingAmount + overdueAmount;

    const paidPercent = total ? (paidAmount / total) * 100 : 0;
    const pendingPercent = total ? (pendingAmount / total) * 100 : 0;
    const overduePercent = total ? (overdueAmount / total) * 100 : 0;

    const indexOfLastCustomer = currentPage * entriesPerPage;
    const indexOfFirstCustomer = indexOfLastCustomer - entriesPerPage;
    const currentInvoices = summaryInvoices.slice(
        indexOfFirstCustomer,
        indexOfLastCustomer
    );
    const totalPages = Math.ceil(summaryInvoices.length / entriesPerPage);

    if (load) {
        return (
            <OmsSpinnerData />
        );
    }


    return (
        <>
            <div className="customers-header">
                <div className="customer-dropdown">
                    <p className="dashboard-title">
                        Customer name
                    </p>
                </div>
                <div className="custom-header-ul">
                    {isDropdownOpen && (
                        <div className="customer-dropdown-list">
                            {/* Search Bar */}
                            {/* <div className="input-container">
                                    <i className="fas fa-search"></i>
                                    <input
                                        type="search"
                                        name="search"
                                        id="search"
                                        placeholder="Search here..."
                                        value={searchQuery}
                                        onChange={handleSearch}
                                    />
                                </div> */}

                            {/* Default Filters */}
                            <div className="customer-dropdown-list-default">
                                <p className="default-filter">
                                    <i className="fa-solid fa-chevron-right"></i>
                                    Default Filters
                                </p>
                                <ul className="customer-list-ul">
                                    <li onClick={() => setSelectedFilter("All invoice")}>All invoice</li>
                                    <li onClick={() => setSelectedFilter("Active invoice")}>Active invoice</li>
                                    <li onClick={() => setSelectedFilter("Overdue invoice")}>Overdue invoice</li>
                                    <li onClick={() => setSelectedFilter("Paid invoice")}>Paid invoice</li>
                                </ul>
                            </div>

                            {/* Added by Me */}
                            {/* <div className="customer-dropdown-list-added">
                                    <p className="default-filter">
                                        <i className="fa-solid fa-chevron-right"></i>
                                        Added by Me
                                    </p>
                                    <ul className="customer-list-ul">
                                        <li onClick={() => setSelectedFilter("My invoice")}>My invoice</li>
                                        <li onClick={() => setSelectedFilter("My Active invoice")}>My Active invoice</li>
                                        <li onClick={() => setSelectedFilter("My Overdue invoice")}>My Overdue invoice</li>
                                        <li onClick={() => setSelectedFilter("My Paid invoice")}>My Paid invoice</li>
                                    </ul>
                                </div> */}
                        </div>
                    )}
                </div>

                <div className="customer-warning-new-hamburger-help">
                    <div
                        className='text-black size-8 rounded-full flex justify-center items-center bg-gray-100 hover:bg-gray-200 cursor-pointer'
                        onClick={() => setShowSearchBar(!showSearchBar)}
                    >
                        <Search size={18} />
                    </div>
                    <div className="filter-controls">
                        <button onClick={() => setShowDrawer(true)} className="more-filters-btn">
                            <i className="fa-solid fa-sliders"></i> Filters
                        </button>
                    </div>
                    <div className="import-hamburger" onClick={toggleImport}>
                        <i className="fa-solid fa-ellipsis-vertical"></i>
                    </div>
                    {isImportOpen && (
                        <div className="import-ul">
                            <ul>
                                <li onClick={() => navigate('/invoice-import')}>
                                    {/* <Link to='/invoice-import'> */}
                                    <i className="fa-solid fa-file-arrow-down"></i> Import invoice
                                    {/* </Link> */}
                                </li>
                            </ul>
                            <ul>
                                <li onClick={() => navigate('/export')}>
                                    {/* <Link to='/export'> */}
                                    <i className="fa-solid fa-file-arrow-up"></i> Export invoice
                                    {/* </Link> */}
                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <a
                                        href="/bad-debt"
                                        style={{ textDecoration: "none" }}
                                    >
                                        <i className="fa-solid fa-file-arrow-down"></i> Bad Debt
                                    </a>

                                </li>
                            </ul>
                            <ul>
                                <li>
                                    <a
                                        href="/sample_invoice.xlsx"
                                        download
                                        style={{ textDecoration: "none" }}
                                    >
                                        <i className="fa-solid fa-file-arrow-down"></i> Download Sample
                                    </a>

                                </li>
                            </ul>
                            <ul>
                                <li onClick={handleRefresh}>
                                    <i className="fa-solid fa-rotate"></i> Refresh List
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {showSearchBar && (
                <div className="w-full my-3 p-0">
                    <div className="relative w-full">
                        <input
                            type="text"
                            placeholder="Search invoice..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                            className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            onClick={() => {
                                setShowSearchBar(false);
                                setSearchQuery('');
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            <X size={18} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            )}

            {/* payment summary */}
            {/* <div className="payment-summary">
                    <p className="payment-head">
                        Payment Summary
                    </p>
                    <div className="payment-summary-details">
                        <div className="payment-summary-list list1">
                            <i className="fa-solid fa-arrow-down"></i>
                            <div className="details">
                                <p className="label">Total receivables pending</p>
                                <h3>Rs. {totalReceivablesPending}</h3>
                            </div>
                        </div>

                        <div className="payment-summary-list list2">
                            <div className="payment">
                                <p className="label">Due as of today</p>
                                <h3>Rs. {Number(dueTodayPendingTotal)}</h3>

                            </div>

                            <div className="payment">
                                <p className="label">Payable within 30 days</p>
                                <h3>Rs. {payableWithin30DaysTotal}</h3>
                            </div>

                            <div className="payment">
                                <p className="label">Invoices past due</p>
                                <h3>Rs. {overduePendingTotal}</h3>
                            </div>

                            <div className="payment">
                                <p className="label">Average payment cycle</p>
                                <h3>₹800.28</h3>
                            </div> 
                        </div>
                    </div>
                </div> */}

            {/* <div className="filter-search">
                    <div className="filter-search-input">
                        <input
                            type="search"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                        <i className={`fa fa-search ${searchQuery ? 'hidden' : ''}`}></i> 
                    </div>
                </div> */}

            <div className="bg-white rounded-lg shadow p-4 mt-4">
                {/* Header Row: Title + Filter */}
                <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
                    <h2 className="text-lg font-semibold">Summary</h2>
                </div>

                {/* Invoice Count */}
                <p className="text-sm text-gray-500 mb-2">
                    Showing <span className="font-medium">{summaryInvoices.length}</span> invoices.
                </p>

                {/* Progress Bar */}
                {/* Animated Progress Bar */}
                <div className="w-full flex rounded overflow-hidden h-4 mb-3 bg-gray-200">
                    <motion.div
                        className="bg-green-500 h-full"
                        initial={false}
                        animate={{ width: `${paidPercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Paid: ₹${paidAmount.toLocaleString()}`}
                    />
                    <motion.div
                        className="bg-yellow-400 h-full"
                        initial={false}
                        animate={{ width: `${pendingPercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Pending: ₹${pendingAmount.toLocaleString()}`}
                    />
                    <motion.div
                        className="bg-red-500 h-full"
                        initial={false}
                        animate={{ width: `${overduePercent}%` }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        title={`Overdue: ₹${overdueAmount.toLocaleString()}`}
                    />
                </div>


                {/* Labels */}
                <div className="flex justify-between items-center text-sm text-gray-700 font-medium">
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-green-500"></span> Paid (₹{paidAmount.toLocaleString()})</span>
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-yellow-400"></span> Pending (₹{pendingAmount.toLocaleString()})</span>
                    <span className='text-center'><span className="inline-block w-3 h-3 rounded-full bg-red-500"></span> Overdue (₹{overdueAmount.toLocaleString()})</span>
                </div>
            </div>



            {/* invoice table */}
            <div className="customer-table responsive-table">
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Order No.</th>
                            <th>Invoice No.</th>
                            <th>Customer Name</th>
                            <th>Due Date</th>
                            <th>P.O. Number</th>
                            <th>Amount</th>
                            <th>Balance Due</th>
                            <th>D/P</th>
                            <th>Status</th>
                            <th>Invoice</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="11"><SkeletonLoader /></td>
                            </tr>
                        ) : currentInvoices.length === 0 ? (
                            <tr>
                                <td colSpan="11" style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                                    No Invoice Available
                                </td>
                            </tr>
                        ) : (
                            currentInvoices.map((invoice, index) => (
                                <tr key={index} className='group'>
                                    <td>{safeDate(invoice.invoice_date)}</td>
                                    <td>#{invoice.order_number || "N/A"}</td>
                                    <td>{invoice.invoice_number || "N/A"}</td>
                                    <td>
                                        {invoice.customer_name || "N/A"}-{invoice.customer_id || "N/A"}
                                        <p className='capitalize text-xs text-blue-600 opacity-0 group-hover:opacity-100 hover:underline cursor-pointer'>view customer</p>
                                    </td>
                                    <td>{safeDate(invoice.due_date)}</td>
                                    <td>{invoice.po_number || "N/A"}</td>
                                    <td>
                                        Rs. {invoice.grand_total
                                            || "0.00"}
                                    </td>
                                    <td>
                                        Rs. {invoice.pending
                                            || "0.00"}
                                        {/* <p className='capitalize text-xs text-red-600 opacity-0 group-hover:opacity-100 hover:underline cursor-pointer'>bad debt</p> */}
                                    </td>
                                    <td>{invoice.dpstatus || "N/A"}</td>
                                    <td
                                        style={{
                                            color: STATUS_COLORS[backendStatusLabel(invoice)] || "gray",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {backendStatusLabel(invoice)}
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        {downloadingId === invoice.order_number ? (
                                            <div className="circle-loader"></div>
                                        ) : downloadedId === invoice.order_number ? (
                                            <i className="fa-solid fa-circle-down downloaded-icon"></i>
                                        ) : (
                                            <>
                                                <span
                                                    onClick={() => downloadInvoicePDF(invoice)}
                                                    className="download-text"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    Download
                                                </span>
                                                {/* <span
                                                   
                                                    className="download-text"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    view
                                                </span> */}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                type="button"
                                                onClick={() => handleCreateReceipt(invoice)}
                                                title="Generate Receipt"
                                            >
                                                <i className="fa-solid fa-receipt"></i>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => viewInvoicePDF(invoice)}
                                                className="delete-btn"
                                            >
                                                <i className="fa-solid fa-eye"></i>
                                            </button>
                                            <button type="button" onClick={() => handleEdit(invoice)}>
                                                <i className="fa-solid fa-pencil"></i>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDelete(invoice.id)}
                                                className="delete-btn"
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))

                        )}
                    </tbody>
                </table>
            </div>
            <div className="ecom-responsive-cards">
                {currentInvoices.length === 0 ? (
                    <p style={{ textAlign: "center", margin: "50px" }}>No Invoice Available</p>
                ) : (
                    currentInvoices.map((invoice, index) => (
                        <div className="ecom-responsive-card" key={index}
                            onClick={() => navigate(`/invoice-detail/${invoice.id}`)}
                            style={{ cursor: "pointer" }}>
                            <div className="ecom-responsive-header">
                                <div className='flex items-center gap-2'>
                                    <p>{invoice.invoice_number || "N/A"}</p>
                                    <span
                                        className={`cursor-pointer ${downloadingId === invoice.order_number ? "opacity-50 cursor-not-allowed" : ""}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (downloadingId) return;
                                            downloadInvoicePDF(invoice);
                                        }}
                                        title="Download invoice"
                                    >
                                        {downloadingId === invoice.order_number ? (
                                            <svg className="animate-spin h-5 w-5 text-purple-700" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor"
                                                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z">
                                                </path>
                                            </svg>
                                        ) : (
                                            <Download size={18} />
                                        )}
                                    </span>
                                    <span
                                        className="cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCreateReceipt(invoice);
                                        }}
                                        title="Generate Receipt"
                                    >
                                        <i className="fa-solid fa-receipt" style={{ color: 'var(--blue)', fontSize: 18 }}></i>
                                    </span>
                                </div>
                                <p>{safeDate(invoice.invoice_date)}</p>
                            </div>
                            <div className="ecom-responsive-body">
                                <div className="ecom-responsive-left">
                                    <h2>{invoice.customer_name || "N/A"}</h2>
                                    <p className='capitalize text-xs text-blue-600 cursor-pointer underline'>view customer</p>
                                    <p>Due date: {safeDate(invoice.due_date)}</p>
                                    <p>P.O. Number: {invoice.po_number || "N/A"}</p>
                                </div>
                                <div className="ecom-responsive-right">
                                    <h4>Rs. {invoice.grand_total
                                        || "0.00"}</h4>
                                    <p>Due amt.: Rs. {invoice.pending
                                        || "0.00"}<br />
                                        <span className='capitalize text-xs text-red-600 cursor-pointer'>bad debt</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>


            {totalPages > 1 && (

                <div className="pagination">
                    {/* First Page */}
                    <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronsLeft size={18} />
                    </button>

                    {/* Previous Page */}
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-chev-btn"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    {/* Current Page Number Only */}
                    <button className="pagination-btn active">
                        {currentPage}
                    </button>

                    {/* Next Page */}
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronRight size={18} />
                    </button>

                    {/* Last Page */}
                    <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className="pagination-chev-btn"
                    >
                        <ChevronsRight size={18} />
                    </button>
                </div>
            )}
            <FilterDrawer />

            <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
                <button
                    onClick={() => navigate("/new-invoice")}
                    className="cssbuttons-io-button">
                    <span>Create Invoice</span>
                </button>
            </div>
        </>
    );
};

export default IndividualInvoice;

export const renderNoteAsPDF = (html) => {
    const doc = parseDocument(html);
    const nodes = DomUtils.getChildren(doc);

    const renderNodes = (elements, depth = 0) => {
        return elements.map((node, idx) => {
            if (node.type === 'text') {
                return node.data;
            }

            if (node.name === 'br') {
                return <Text key={`br-${depth}-${idx}`}>{'\n'}</Text>;
            }

            if (node.name === 'p') {
                return (
                    <Text key={`p-${depth}-${idx}`} style={styles.paragraph}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }
            if (node.name === 'a') {
                const href = node.attribs?.href || "#";
                const linkText = DomUtils.getText(node); // 🔥 Plain text from inside <a>
                return (
                    <Link
                        key={`a-${depth}-${idx}`}
                        src={href.startsWith("http") ? href : `https://${href}`} // ensure it's clickable
                        style={{ color: 'blue', textDecoration: 'none' }} // removes underline
                    >
                        {linkText}
                    </Link>
                );
            }


            if (node.name === 'u') {
                return (
                    <Text key={`u-${depth}-${idx}`} style={{ textDecoration: 'underline' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }



            if (node.name === 'strong' || node.name === 'b') {
                return (
                    <Text key={`b-${depth}-${idx}`} style={{ fontWeight: 'bold' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'em' || node.name === 'i') {
                return (
                    <Text key={`i-${depth}-${idx}`} style={{ fontStyle: 'italic' }}>
                        {renderNodes(DomUtils.getChildren(node), depth + 1)}
                    </Text>
                );
            }

            if (node.name === 'ul') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ul-${depth}-${idx}-${i}`} style={styles.listItem}>
                            • {renderNodes(DomUtils.getChildren(li), depth + 1)}
                        </Text>
                    ));
            }

            if (node.name === 'ol') {
                return DomUtils.getChildren(node)
                    .filter(n => n.name === 'li')
                    .map((li, i) => (
                        <Text key={`ol-${depth}-${idx}-${i}`} style={styles.listItem}>
                            {`${i + 1}. ${renderNodes(DomUtils.getChildren(li), depth + 1)}`}
                        </Text>
                    ));
            }

            // fallback for unknown tags
            return renderNodes(DomUtils.getChildren(node), depth + 1);
        });
    };

    return renderNodes(nodes);
};


export const renderQuillToPDF = (html) => {
    if (!html) return <Text>No Notes</Text>;

    const doc = parseDocument(html);

    const renderNode = (node, key = 0) => {

        if (node.type === "text") {
            return <Text key={key}>{node.data}</Text>;
        }

        if (!node.name) return null;

        const children = (node.children || []).map((child, i) =>
            renderNode(child, i)
        );

        switch (node.name) {

            case "p":
                return <Text key={key} style={{ marginBottom: 4 }}>{children}</Text>;

            case "strong":
            case "b":
                return <Text key={key} style={{ fontWeight: "bold" }}>{children}</Text>;

            case "em":
            case "i":
                return <Text key={key} style={{ fontStyle: "italic" }}>{children}</Text>;

            case "u":
                return <Text key={key} style={{ textDecoration: "underline" }}>{children}</Text>;

            case "br":
                return <Text key={key}>{"\n"}</Text>;

            case "ul":
                return (
                    <View key={key} style={{ marginBottom: 4 }}>
                        {node.children.map((li, i) => (
                            <Text key={i}>• {li.children?.[0]?.data}</Text>
                        ))}
                    </View>
                );

            case "ol":
                return (
                    <View key={key} style={{ marginBottom: 4 }}>
                        {node.children.map((li, i) => (
                            <Text key={i}>{i + 1}. {li.children?.[0]?.data}</Text>
                        ))}
                    </View>
                );

            case "a":
                return (
                    <Link
                        key={key}
                        src={node.attribs?.href}
                        style={{
                            color: "#007bff",
                            textDecoration: "underline"
                        }}
                    >
                        {children}
                    </Link>
                );

            default:
                return <Text key={key}>{children}</Text>;
        }
    };

    return doc.children.map((node, i) => renderNode(node, i));
};

const calculateGST = (taxable, taxRate) => {
    const rate = Number(taxRate) || 0;

    if (rate === 0) {
        return {
            cgstRate: 0,
            sgstRate: 0,
            cgst: 0,
            sgst: 0,
        };
    }

    const halfRate = rate / 2;

    return {
        cgstRate: halfRate,
        sgstRate: halfRate,
        cgst: (taxable * halfRate) / 100,
        sgst: (taxable * halfRate) / 100,
    };
};

/** Line total incl. tax — matches ItemTable / NewInvoice payload */
const pdfLineAmount = (item) => {
    const qty = Number(item?.quantity) || 0;
    const rate = Number(item?.rate) || 0;
    const discount = Number(item?.discount) || 0;
    const tax = Number(item?.tax) || 0;
    if (qty && rate) {
        return qty * rate * (1 - discount / 100) * (1 + tax / 100);
    }
    return Number(item?.amount) || 0;
};

/** Taxable value before GST (after line discount) */
const pdfLineTaxable = (item) => {
    const qty = Number(item?.quantity) || 0;
    const rate = Number(item?.rate) || 0;
    const discount = Number(item?.discount) || 0;
    return qty * rate * (1 - discount / 100);
};

/** Line discount amount in currency */
const pdfLineDiscount = (item) => {
    const qty = Number(item?.quantity) || 0;
    const rate = Number(item?.rate) || 0;
    const discount = Number(item?.discount) || 0;
    return qty * rate * (discount / 100);
};

const numberToIndianWords = (num) => {
    if (!num || isNaN(num)) return "Zero Rupees";

    const a = [
        "", "One", "Two", "Three", "Four", "Five", "Six",
        "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
        "Thirteen", "Fourteen", "Fifteen", "Sixteen",
        "Seventeen", "Eighteen", "Nineteen",
    ];

    const b = [
        "", "", "Twenty", "Thirty", "Forty",
        "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
    ];

    const inWords = (n) => {
        if (n < 20) return a[n];
        if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
        if (n < 1000)
            return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + inWords(n % 100) : "");
        return "";
    };

    let number = Math.floor(num);
    let words = "";

    if (number >= 10000000) {
        words += inWords(Math.floor(number / 10000000)) + " Crore ";
        number %= 10000000;
    }
    if (number >= 100000) {
        words += inWords(Math.floor(number / 100000)) + " Lakh ";
        number %= 100000;
    }
    if (number >= 1000) {
        words += inWords(Math.floor(number / 1000)) + " Thousand ";
        number %= 1000;
    }
    if (number > 0) {
        words += inWords(number);
    }

    return words.trim() + " Rupees";
};


/* ---------------- STYLES ---------------- */
const styles = StyleSheet.create({
    page: {
        padding: 16,
        fontSize: 9,
        fontFamily: "Helvetica",
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
    },

    box: {
        borderWidth: 1,
        borderColor: "#000",
    },

    cell: {
        padding: 6,
    },

    bold: {
        fontWeight: "bold",
    },
    gstTable: {
        marginTop: 8,
        borderWidth: 1,
        borderColor: "#000",
    },

    gstRow: {
        flexDirection: "row",
    },

    gstCell: {
        padding: 6,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#000",
        textAlign: "center",
    },
    semibold: {
        fontWeight: 500,
        marginBottom: 5,
    },
    gstHeader: {
        backgroundColor: "#eaeaea",
        fontWeight: "bold",
        textAlign: "center",
    },

    gstRight: {
        textAlign: "right",
    },

    gstBold: {
        fontWeight: "bold",
    },
    itemTable: {
        borderWidth: 1,
        borderColor: "#000",
    },

    itemRow: {
        flexDirection: "row",
    },

    itemHeaderCell: {
        padding: 6,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: "#000",
        backgroundColor: "#f2f2f2",
        fontWeight: "bold",
        textAlign: "center",
    },

    itemCell: {
        padding: 6,
        borderRightWidth: 1,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },
    itemlastcelltable: {
        padding: 6,
        borderRightWidth: 1,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },

    itemLastCell: {
        padding: 6,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },
    itemLastCelltable: {
        padding: 6,
        // borderBottomWidth: 1,
        borderColor: "#000",
    },

    marginTopitemCell: {
        marginTop: 4
    },


});

/* ---------------- DOCUMENT ---------------- */
const InvoiceDocument = ({ invoice, profile, logoBase64, signUrl, bankDetails, authorityData, base64Sign, customer }) => {
    const safeProfile = profile && typeof profile === "object" ? profile : {};
    const safeBank =
        bankDetails && typeof bankDetails === "object" ? bankDetails : {};
    const companyName = authorityData?.company_name ?? "";
    const safeCustomer =
        customer && typeof customer === "object" ? customer : {};

    let items = [];
    try {
        if (Array.isArray(invoice?.items)) {
            items = invoice.items;
        } else if (typeof invoice?.items === "string") {
            items = JSON.parse(invoice.items || "[]");
        }
    } catch {
        items = [];
    }
    if (!Array.isArray(items)) items = [];

    const parseAddress = (raw) => {
        if (raw == null) return null;
        if (typeof raw === "object" && !Array.isArray(raw)) return raw;
        if (typeof raw !== "string") return null;
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === "object" ? parsed : null;
        } catch {
            return null;
        }
    };

    const formatAddress = (addr) => {
        if (!addr) return "-";

        const parts = [
            addr.address,
            addr.city,
            addr.state,
            addr.pincode,
            addr.country,
        ].filter(Boolean);

        return parts.length ? parts.join(", ") : "-";
    };


    const totals = items.reduce(
        (acc, i) => {
            const taxable = pdfLineTaxable(i);
            const gst = calculateGST(taxable, i.tax);
            acc.taxable += taxable;
            acc.cgst += gst.cgst;
            acc.sgst += gst.sgst;
            acc.linesTotal += pdfLineAmount(i);
            return acc;
        },
        { taxable: 0, cgst: 0, sgst: 0, linesTotal: 0 }
    );

    const GST_COLS = {
        hsn: 80,
        taxable: 120,
        rate: 50,
        amount: 70,
        total: 120,
    };

    const shipping = Number(invoice.shipping || 0);
    const computedGrand = totals.linesTotal + shipping;
    const storedGrand = Number(
        invoice.grand_total ?? invoice.total_grand_total ?? 0
    );
    const grandTotal =
        !Number.isNaN(storedGrand) && storedGrand > 0
            ? storedGrand
            : computedGrand;

    const gstRate =
        items.length > 0 ? Number(items[0].tax) || 0 : 0;

    const cgstRate = gstRate / 2;
    const sgstRate = gstRate / 2;

    const grandTotalWords = numberToIndianWords(grandTotal);

    const billingAddrObj = parseAddress(safeCustomer?.billingAddress);
    const shippingAddrObj = parseAddress(safeCustomer?.shippingAddress);

    const billingAddress = formatAddress(billingAddrObj);
    const shippingAddress = formatAddress(shippingAddrObj);

    const billToDisplayName =
        safeCustomer.companyName ||
        safeCustomer.displayName ||
        safeCustomer.customerName ||
        invoice.customer_name ||
        "";

    const customerGstin =
        safeCustomer.gstNumber ||
        safeCustomer.gst_number ||
        "";

    const customerPhonesLine = [safeCustomer.phone1, safeCustomer.phone2]
        .map((p) => (p != null ? String(p).trim() : ""))
        .filter(Boolean)
        .join(" / ");

    const placeOfSupplyFromBilling = [
        billingAddrObj?.city,
        billingAddrObj?.state,
        billingAddrObj?.pincode,
    ]
        .filter(Boolean)
        .join(", ");

    const placeOfSupplyLine =
        placeOfSupplyFromBilling ||
        [safeProfile.city, safeProfile.state, safeProfile.pincode]
            .filter(Boolean)
            .join(", ");

    const RUPEE = "\u20B9";


    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* ================= HEADER ================= */}
                <View style={[styles.row, { marginBottom: 6 }]}>
                    {/* TAX INVOICE (NO BOX) */}
                    <Text style={[styles.bold, { fontSize: 12 }]}>TAX INVOICE</Text>

                    {/* ORIGINAL FOR RECIPIENT (BOXED) */}
                    <View style={[styles.box, { marginLeft: 10, paddingHorizontal: 6, paddingVertical: 2 }]}>
                        <Text>ORIGINAL FOR RECIPIENT</Text>
                    </View>
                </View>

                {/* ================= COMPANY + INVOICE ================= */}
                <View style={[styles.box, styles.row]}>

                    {/* -------- LEFT : COMPANY -------- */}
                    <View style={[styles.cell, { width: "60%", borderRightWidth: 1 }]}>

                        <View style={styles.row}>
                            {logoBase64 && (
                                <Image
                                    src={logoBase64}
                                    style={{ width: 48, height: 48, marginRight: 8 }}
                                />
                            )}

                            <View style={{ width: "100%" }}>
                                <Text style={[styles.bold, { fontSize: 12 }]}>
                                    {companyName}
                                </Text>

                                <Text style={[{ fontSize: 10 }]}>
                                    {safeProfile.office_address ?? ""}
                                </Text>
                                {
                                    safeProfile.taxNo &&
                                    <Text style={[{ fontSize: 10 }]}>GSTIN: {safeProfile.taxNo}</Text>
                                }

                                {
                                    safeProfile.mobile &&
                                    <Text style={[{ fontSize: 10 }]}>Mobile: {safeProfile.mobile}</Text>
                                }

                                {safeProfile.pan &&
                                    <Text style={[{ fontSize: 10 }]}>PAN Number: {safeProfile.pan}</Text>
                                }

                            </View>
                        </View>

                    </View>

                    {/* -------- RIGHT : INVOICE INFO -------- */}
                    <View style={{ width: "40%" }}>

                        {/* Row 1 */}
                        <View style={[styles.row, { borderBottomWidth: 0 }]}>
                            <View style={[styles.cell, { width: "50%", borderRightWidth: 0 }]}>
                                <Text style={styles.bold}>Invoice No.</Text>
                                <Text>{invoice.invoice_number}</Text>
                            </View>

                            <View style={[styles.cell]}>
                                <Text style={styles.bold}>Invoice Date</Text>
                                <Text>{invoice.invoice_date}</Text>
                            </View>
                        </View>

                        {/* Row 2 */}
                        <View style={styles.row}>
                            <View style={[styles.cell, { width: "100%", borderRightWidth: 0 }]}>
                                <Text style={styles.bold}>P.O. Number</Text>
                                <Text>{invoice.po_number || "-"}</Text>
                            </View>
                        </View>

                    </View>
                </View>
                {/* ================= BILL TO / SHIP TO ================= */}
                <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>

                    {/* LEFT = 60% (same as company block) */}
                    <View
                        style={[
                            styles.cell,
                            {
                                width: "60%",
                                borderRightWidth: 1,   // ✅ aligns with company divider
                                borderColor: "#000",
                            },
                        ]}
                    >
                        <Text style={styles.semibold}>BILL TO</Text>
                        <Text style={[styles.bold, { fontSize: 10 }]}>
                            {billToDisplayName}
                        </Text>
                        {billingAddress && billingAddress !== "-" && (
                            <Text style={{ fontSize: 10 }}>
                                Address: {billingAddress}
                            </Text>
                        )}
                        {customerGstin ? (
                            <Text style={{ fontSize: 10 }}>GSTIN: {customerGstin}</Text>
                        ) : null}
                        {customerPhonesLine ? (
                            <Text style={{ fontSize: 10 }}>
                                Phone: {customerPhonesLine}
                            </Text>
                        ) : null}
                        {placeOfSupplyLine ? (
                            <Text style={{ fontSize: 10 }}>
                                Place of Supply: {placeOfSupplyLine}
                            </Text>
                        ) : null}

                    </View>

                    {/* RIGHT = 40% */}
                    <View style={{ width: "40%", padding: 6 }}>
                        <Text style={styles.semibold}>SHIP TO</Text>
                        <Text style={[{ fontSize: 10 }]}>
                            Address: {shippingAddress}
                        </Text>
                        {customerPhonesLine ? (
                            <Text style={[{ fontSize: 10 }]}>
                                Phone: {customerPhonesLine}
                            </Text>
                        ) : null}
                    </View>

                </View>




                {/* ================= ITEMS TABLE (IMAGE 2) ================= */}
                <View style={[styles.itemTable, { borderTopWidth: 0, borderBottomWidth: 0 }]}>
                    {/* ---------- HEADER ---------- */}
                    <View style={[styles.itemRow]}>
                        <Text style={[styles.itemHeaderCell, { width: 30 }]}>NO.</Text>
                        <Text style={[styles.itemHeaderCell, { width: 260 }]}>ITEMS / SERVICES</Text>
                        <Text style={[styles.itemHeaderCell, { width: 80 }]}>HSN/SAC</Text>
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>QTY.</Text>
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>RATE</Text>
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>DIS.</Text>
                        <Text style={[styles.itemLastCell, styles.itemHeaderCell, { width: 80, borderRightWidth: 0 }]}>AMOUNT</Text>
                    </View>



                    {items.map((i, idx) => {
                        const qty = Number(i.quantity) || 0;
                        const rate = Number(i.rate) || 0;
                        const lineAmount = pdfLineAmount(i);
                        const lineDiscount = pdfLineDiscount(i);

                        return (
                            <View key={idx} style={styles.itemRow}>
                                <Text style={[styles.itemCell, { width: 30, fontSize: 8 }]}>{idx + 1}</Text>

                                <Text style={[styles.itemCell, { width: 260, fontSize: 8 }]}>
                                    {(() => {
                                        const it = i?.item;
                                        const raw =
                                            typeof it === "string"
                                                ? it
                                                : it?.title != null
                                                  ? String(it.title)
                                                  : String(i?.description || "Item");
                                        return raw
                                            .replace(/\s*-\s*\n?\s*(VP|SM|GP|SKU)[A-Z0-9-]*/i, "")
                                            .trim();
                                    })()}
                                </Text>


                                <Text style={[styles.itemCell, { width: 80, fontSize: 8 }]}>{i?.hsn || "-"}</Text>

                                <Text style={[styles.itemCell, { width: 60, textAlign: "center", fontSize: 8 }]}>
                                    {qty}
                                </Text>

                                <Text style={[styles.itemCell, { width: 60, textAlign: "right", fontSize: 8 }]}>
                                    {rate.toFixed(2)}
                                </Text>
                                <Text style={[styles.itemCell, { width: 60, textAlign: "right", fontSize: 8 }]}>
                                    {lineDiscount.toFixed(2)}
                                </Text>
                                <Text style={[styles.itemLastCell, { width: 80, textAlign: "right", fontSize: 8 }]}>
                                    {lineAmount.toFixed(2)}
                                </Text>
                            </View>
                        );
                    })}



                    {/* ---------- BLANK FILLER (keeps CGST at bottom) ---------- */}
                    <View style={[styles.itemRow, { height: 160 }]}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, { width: 260 }]} />
                        <Text style={[styles.itemCell, { width: 80 }]} />
                        <Text style={[styles.itemCell, { width: 60 }]} />
                        <Text style={[styles.itemCell, { width: 60 }]} />
                        <Text style={[styles.itemCell, { width: 60 }]} />


                        <Text style={[styles.itemLastCell, { width: 80 }]} />
                    </View>

                    {/* ---------- CGST ---------- */}
                    <View style={styles.itemRow}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, styles.gstRight, { width: 260 }]}>CGST @{cgstRate}%</Text>
                        <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0 }]}>Rs. {totals.cgst.toFixed(2)}</Text>
                    </View>

                    {/* ---------- SGST ---------- */}
                    <View style={styles.itemRow}>
                        <Text style={[styles.itemCell, { width: 30 }]} />
                        <Text style={[styles.itemCell, styles.gstRight, { width: 260 }]}> SGST @{sgstRate}%</Text>
                        <Text style={[styles.itemCell, { width: 80 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0 }]}>Rs. {totals.sgst.toFixed(2)}</Text>
                    </View>

                    {/* ---------- SHIPPING ---------- */}
                    {shipping > 0 && (
                        <View style={styles.itemRow}>
                            <Text style={[styles.itemCell, { width: 30 }]} />
                            <Text style={[styles.itemCell, styles.gstRight, { width: 260 }]}>SHIPPING</Text>
                            <Text style={[styles.itemCell, { width: 80 }]} />
                            <Text style={[styles.itemCell, { width: 60 }]} />
                            <Text style={[styles.itemCell, { width: 60 }]} />
                            <Text style={[styles.itemCell, { width: 60 }]} />
                            <Text style={[styles.itemLastCell, styles.gstRight, { width: 80, borderTopWidth: 0 }]}>
                                Rs. {shipping.toFixed(2)}
                            </Text>
                        </View>
                    )}

                    {/* ---------- TOTAL ---------- */}
                    <View style={[styles.itemRow, { borderTopWidth: 1 }]}>
                        <Text style={[styles.itemHeaderCell, { width: 30 }]} />
                        <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 260 }]}>TOTAL</Text>
                        <Text style={[styles.itemHeaderCell, { width: 80 }]} />
                        <Text style={[styles.itemHeaderCell, { width: 60 }]} />
                        <Text style={[styles.itemHeaderCell, { width: 60 }]} />
                        <Text style={[styles.itemHeaderCell, { width: 60 }]}>-</Text>
                        <Text style={[styles.itemHeaderCell, styles.bold, styles.gstRight, { width: 80, borderRightWidth: 0 }]}>Rs. {grandTotal.toFixed(2)}</Text>
                    </View>

                </View>

                {/* ================= GST SUMMARY TABLE ================= */}
                <View style={[styles.gstTable, { borderRightWidth: 0 }]} wrap={false}>

                    {/* HEADER ROW 1 */}
                    <View style={[styles.gstRow]}>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]}>
                            HSN/SAC
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]}>
                            Taxable Value
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
                            CGST
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate + GST_COLS.amount }]}>
                            SGST
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]}>
                            Total Tax Amount
                        </Text>
                    </View>


                    {/* HEADER ROW 2 */}
                    <View style={[styles.gstRow, { borderBottomWidth: 0 }]}>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.hsn }]} />
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.taxable }]} />

                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
                            Rate
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
                            Amount
                        </Text>

                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.rate }]}>
                            Rate
                        </Text>
                        <Text style={[styles.gstCell, styles.gstHeader, { width: GST_COLS.amount }]}>
                            Amount
                        </Text>

                        <Text style={[styles.gstCell, styles.gstHeader, { flex: 1 }]} />

                    </View>


                    {/* DATA ROWS */}
                    {items.map((i, idx) => {
                        const taxable = pdfLineTaxable(i);
                        const gst = calculateGST(taxable, i.tax);
                        const taxRate = Number(i.tax) || 0;

                        return (
                            <View key={idx} style={styles.gstRow}>
                                <Text style={[styles.gstCell, { width: GST_COLS.hsn }]}>{i.hsn || "-"}</Text>

                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.taxable }]}>
                                    {taxable.toLocaleString()}
                                </Text>

                                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{taxRate / 2}%</Text>
                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                                    {gst.cgst.toFixed(1)}
                                </Text>

                                <Text style={[styles.gstCell, { width: GST_COLS.rate }]}>{taxRate / 2}%</Text>
                                <Text style={[styles.gstCell, styles.gstRight, { width: GST_COLS.amount }]}>
                                    {gst.sgst.toFixed(1)}
                                </Text>

                                <Text style={[styles.gstCell, styles.gstRight, { flex: 1 }]}>
                                    Rs. {(gst.cgst + gst.sgst).toFixed(1)}
                                </Text>


                            </View>
                        );
                    })}

                    {/* TOTAL ROW */}
                    <View style={styles.gstRow}>
                        <Text style={[styles.gstCell, styles.gstBold, { width: GST_COLS.hsn, borderBottomWidth: 0 }]}>
                            Total
                        </Text>

                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.taxable, borderBottomWidth: 0 }]}>
                            {totals.taxable.toLocaleString()}
                        </Text>

                        <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
                            {totals.cgst.toFixed(1)}
                        </Text>

                        <Text style={[styles.gstCell, { width: GST_COLS.rate, borderBottomWidth: 0 }]} />
                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { width: GST_COLS.amount, borderBottomWidth: 0 }]}>
                            {totals.sgst.toFixed(1)}
                        </Text>

                        <Text style={[styles.gstCell, styles.gstBold, styles.gstRight, { flex: 1, borderBottomWidth: 0 }]}>
                            Rs. {(totals.cgst + totals.sgst).toFixed(1)}
                        </Text>
                    </View>



                </View>
                {/* ================= TOTAL IN WORDS ================= */}
                <View style={[styles.box, { marginTop: 8, padding: 6 }]}>
                    <Text style={styles.bold}>Total Amount (in words)</Text>
                    <Text style={{ marginTop: 4 }}>
                        {grandTotalWords}
                    </Text>
                </View>

                {/* ================= FOOTER ================= */}
                <View style={[styles.box, styles.row, { borderTopWidth: 0 }]}>
                    {/* <View
                        style={{
                            width: "40%",
                            padding: 6,
                            borderRightWidth: 1,
                            alignSelf: "stretch",
                        }}
                    >

                        <Text style={styles.bold}>Notes</Text>
                        <Text style={styles.marginTopitemCell}> {renderNoteAsPDF(invoice.customer_note || '<p>No notes</p>')}</Text>
                    </View> */}

                    <View
                        style={{
                            width: "50%",
                            padding: 6,
                            borderRightWidth: 1,
                            alignSelf: "stretch",
                        }}
                    >

                        <Text style={styles.bold}>Bank Details</Text>
                        <Text style={styles.marginTopitemCell}>Name: {safeBank.bank_name ?? ""}</Text>
                        <Text style={styles.marginTopitemCell}>IFSC Code: {safeBank.ifsc_code ?? ""}</Text>
                        <Text style={styles.marginTopitemCell}>Account No: {safeBank.account_no ?? ""}</Text>
                        <Text style={styles.marginTopitemCell}>Bank: {safeBank.bank ?? ""}</Text>
                    </View>

                    <View
                        style={{
                            width: "50%",
                            padding: 6,
                            // borderLeftWidth: 1,
                            borderColor: "#000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {base64Sign && <Image src={base64Sign} style={{ width: 80, height: 40 }} />}
                        <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                            Authorised Signatory For{"\n"}

                        </Text>
                        <Text style={[styles.center, { marginTop: 4, fontSize: 8 }]}>
                            {companyName}

                        </Text>

                    </View>
                </View>
                {/* ================= NOTES AT BOTTOM ================= */}
                <View
                    style={{
                        marginTop: 10,
                        paddingTop: 6,
                       
                    }}
                >
                    <Text style={styles.bold}>Notes:</Text>
                    <View style={{ marginTop: 4 }}>
        {renderQuillToPDF(invoice.customer_note || '<p>No notes</p>')}
    </View>
                </View>
            </Page>
        </Document >
    );
};



const InvoicePdf = () => {
    const { order_number } = useParams();
    const [invoiceData, setInvoiceData] = useState(null);
    const url = API_BASE_URL
    useEffect(() => {
        const fetchInvoice = async () => {
            try {
                const res = await fetch(`${url}/newinvoice/all`);
                const data = await res.json();
                const match = data.find(inv => String(inv.order_number) === String(order_number));
                if (match) {
                    setInvoiceData(match);
                } else {
                    alert("Invoice not found.");
                }
            } catch (error) {
                console.error("Error fetching invoice:", error);
            }
        };

        fetchInvoice();
    }, [order_number]);

    useEffect(() => {
        if (invoiceData) {
            const generate = async () => {
                const blob = await pdf(<InvoiceDocument invoice={invoiceData} />).toBlob();
                saveAs(blob, `Invoice_${invoiceData.invoice_number}.pdf`);
            };
            generate();
        }
    }, [invoiceData]);

    return null;
};
